import { encrypt } from '../../../../core/cryptor/cipher';
import { prepareSnapshotData } from '../../helpers';
import {
  SnapshotInfo,
  SnapshotParams,
  SnapshotType,
  walParams,
} from '../../types';
import {
  getSnapshotFileHandlers,
  getSnapshotInfoFS,
  getWALFileHandlers,
} from './utils';
import { saveRecordInUnSyncedDB } from '../../../../core/database/unSynced/unSyncedStorage';
import {
  appendDataInFile,
  readFileContents,
  saveDataInFile,
} from '../../commonUtils';

// FS Structure
// Snapshot File (Contains the main snapshot of data which will be override when a wal is closed)
// SnapshotMetaDataFile (Contains the snaphost info like lastSerial, activeWAL, mergedWAL)
// WAL File (Contains wal data | append)
// WAL MetaData (Contains last added record serial)
// Verified WAL (Contains serverAssignedSerial against local serial | append)

export class FsSheetWriter {
  private async generateLastAssignedSerialInWAL(
    snapMetaHandle: FileSystemFileHandle,
    walHandle: FileSystemFileHandle,
    walMetaHandle: FileSystemFileHandle,
    feKey: CryptoKey
  ) {
    const snapshotInfo: SnapshotInfo = await getSnapshotInfoFS(
      snapMetaHandle,
      feKey
    );
    const records = (await readFileContents(walHandle)).split('\n');
    const recordsCount = Math.max(0, records.length - 1);

    const lastSerial = snapshotInfo.lastSerial + recordsCount;

    await saveDataInFile(walMetaHandle, lastSerial.toString());

    return lastSerial;
  }

  private async readWALMeta(
    fileHandle: FileSystemFileHandle
  ): Promise<number | undefined> {
    const meta = await readFileContents(fileHandle);
    const lastLocalSerial = meta.split('\n')[0]?.trim();
    return lastLocalSerial ? Number(lastLocalSerial) : undefined;
  }

  private async getLastRecordSerial(
    snapMetaHandle: FileSystemFileHandle,
    walHandle: FileSystemFileHandle,
    walMetaHandle: FileSystemFileHandle,
    feKey: CryptoKey
  ) {
    let lastSerial: number | undefined = await this.readWALMeta(walMetaHandle);
    if (!lastSerial) {
      lastSerial = await this.generateLastAssignedSerialInWAL(
        snapMetaHandle,
        walHandle,
        walMetaHandle,
        feKey
      );
    }
    return lastSerial ?? 0;
  }

  private async updateSnapshotInfo(
    directoryHandle: FileSystemDirectoryHandle,
    snapshotMetaFileHandle: FileSystemFileHandle,
    feKey: CryptoKey,
    snapshotInfo: SnapshotInfo
  ) {
    let newLastSerial = 0;
    if (snapshotInfo.activeWAL > 0) {
      const walHandlers = await getWALFileHandlers(
        directoryHandle,
        snapshotInfo.activeWAL
      );
      newLastSerial = await this.getLastRecordSerial(
        snapshotMetaFileHandle,
        walHandlers.wal,
        walHandlers.meta,
        feKey
      );
    }
    const newSnapshotInfo = {
      ...snapshotInfo,
      lastSerial: newLastSerial,
      activeWAL: snapshotInfo.activeWAL + 1,
    };
    const updatedSnapshotInfo = await encrypt(
      JSON.stringify(newSnapshotInfo),
      'snapshot',
      feKey
    );
    await saveDataInFile(snapshotMetaFileHandle, updatedSnapshotInfo);
  }

  public async saveSnapshot(
    parentDirectoryHandle: FileSystemDirectoryHandle,
    feKey: CryptoKey,
    dbName: string,
    snapshotParams: SnapshotParams
  ) {
    const data = prepareSnapshotData(snapshotParams);
    try {
      const snapHandlers = await getSnapshotFileHandlers(
        parentDirectoryHandle,
        dbName
      );
      const encSnap = await encrypt(data, 'snapshot', feKey);
      await saveDataInFile(snapHandlers.snap, encSnap);
      const snapshotInfo = await getSnapshotInfoFS(snapHandlers.meta, feKey);

      await this.updateSnapshotInfo(
        snapHandlers.directoryHandle,
        snapHandlers.meta,
        feKey,
        snapshotInfo
      );
    } catch (err) {
      // TODO: Post Error message
      return;
    }
  }

  public async saveChanges(
    parentDirectoryHandle: FileSystemDirectoryHandle,
    feKey: CryptoKey,
    dbName: string,
    walParams: walParams
  ) {
    let { changes, activeWAL = 0, serverAssignedSerial } = walParams;
    try {
      const snapHandlers = await getSnapshotFileHandlers(
        parentDirectoryHandle,
        dbName
      );
      const encChanges = await encrypt(
        JSON.stringify(changes),
        'changes',
        feKey
      );

      if (activeWAL === 0) {
        const snapshotInfo: SnapshotInfo = await getSnapshotInfoFS(
          snapHandlers.meta,
          feKey
        );
        activeWAL = Math.max(snapshotInfo.activeWAL, 1);
      }

      const walHandlers = await getWALFileHandlers(
        snapHandlers.directoryHandle,
        activeWAL
      );

      const lastRecordSerial = await this.getLastRecordSerial(
        snapHandlers.meta,
        walHandlers.wal,
        walHandlers.meta,
        feKey
      );

      await appendDataInFile(walHandlers.wal, encChanges);

      const localKey = lastRecordSerial + 1;
      await saveDataInFile(walHandlers.meta, localKey.toString());

      if (serverAssignedSerial) {
        await appendDataInFile(
          walHandlers.verifiedSerials,
          `${localKey}:${serverAssignedSerial}`
        );
      } else {
        let id = dbName;
        if (walParams.walType === SnapshotType.EntityIdToName) {
          id = walParams.changes[0]?.path[0]?.toString() ?? dbName; // Set id to sheetId as this sheet id will be used by the server to create and send sheetsIdToName with other users
        }
        // Save WAL Info in UnSynchedWALs DB
        await saveRecordInUnSyncedDB({
          type: walParams.walType,
          activeWAL,
          localSerial: localKey,
          id,
        });
      }
    } catch (err) {
      // TODO: Post MEssage
      return;
    }
  }
}
